import React from 'react';
import PropTypes from 'prop-types';
import { getPlatformSpecs } from '../../constants/platformSpecs';
import CharacterCounter from './CharacterCounter';
import ImageCropPreview from './ImageCropPreview';
import styles from './InstagramPreview.module.scss';

const InstagramPreview = ({
  user,
  content,
  hashtags = [],
  imageUrl,
  onRemoveImage,
}) => {
  const specs = getPlatformSpecs('instagram');
  const truncateAt = specs?.previewTruncateAt || 125;

  const stripHtml = (html) => {
    if (!html) return '';
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/div>/gi, '\n')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ');

    const tmp = document.createElement('DIV');
    tmp.innerHTML = text;
    text = tmp.textContent || tmp.innerText || '';

    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  const plainContent = stripHtml(content);
  const charCount = plainContent.length + hashtags.join(' ').length;
  const shouldTruncate = plainContent.length > truncateAt;
  const displayContent = shouldTruncate
    ? `${plainContent.slice(0, truncateAt)}...`
    : plainContent;

  const getUserName = () => {
    if (!user) return 'user';
    if (user.first_name && user.last_name) {
      return `${user.first_name.toLowerCase()}${user.last_name.toLowerCase()}`;
    }
    return user.name?.toLowerCase().replace(/\s+/g, '') || 'user';
  };

  const getDisplayName = () => {
    if (!user) return 'User';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || 'User';
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.first_name || user.name?.split(' ')[0] || '';
    const lastName = user.last_name || user.name?.split(' ')[1] || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  return (
    <div className={styles.instagramPreview}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.userSection}>
          <div className={styles.avatarWrapper}>
            <div className={styles.avatar}>
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={getDisplayName()} />
              ) : (
                <span>{getUserInitials()}</span>
              )}
            </div>
          </div>
          <div className={styles.userInfo}>
            <span className={styles.userName}>{getUserName()}</span>
          </div>
        </div>
        <button className={styles.moreButton} aria-label="More options">
          {/* Instagram Three Dots */}
          <svg viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="6" cy="12" r="1.5" />
            <circle cx="18" cy="12" r="1.5" />
          </svg>
        </button>
      </div>

      {/* Image */}
      <div className={styles.imageContainer}>
        {imageUrl ? (
          <ImageCropPreview
            imageUrl={imageUrl}
            platformType="instagram"
            onRemove={onRemoveImage}
          />
        ) : (
          <div className={styles.imagePlaceholder}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5-7l-3 3.72L9 13l-3 4h12l-4-5z" />
            </svg>
            <span>Add an image</span>
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className={styles.actionBar}>
        <div className={styles.leftActions}>
          <button className={styles.actionButton} aria-label="Like">
            {/* Instagram Heart Icon */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
            </svg>
          </button>
          <button className={styles.actionButton} aria-label="Comment">
            {/* Instagram Comment Icon */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.656 17.008a9.993 9.993 0 10-3.59 3.615L22 22z" strokeLinejoin="round" />
            </svg>
          </button>
          <button className={styles.actionButton} aria-label="Share">
            {/* Instagram Share/DM Icon */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" fill="none" />
            </svg>
          </button>
        </div>
        <button className={styles.actionButton} aria-label="Save">
          {/* Instagram Bookmark Icon */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
          </svg>
        </button>
      </div>

      {/* Likes */}
      <div className={styles.likes}>
        <span>Be the first to like this</span>
      </div>

      {/* Caption */}
      <div className={styles.caption}>
        <span className={styles.captionUser}>{getUserName()}</span>
        <span className={styles.captionText}>
          {displayContent}
          {shouldTruncate && (
            <span className={styles.more}>more</span>
          )}
        </span>
      </div>

      {/* Hashtags */}
      {hashtags.length > 0 && (
        <div className={styles.hashtags}>
          {hashtags.slice(0, 10).map((tag, index) => (
            <span key={index} className={styles.hashtag}>
              {tag.startsWith('#') ? tag : `#${tag}`}
            </span>
          ))}
          {hashtags.length > 10 && (
            <span className={styles.moreHashtags}>+{hashtags.length - 10} more</span>
          )}
        </div>
      )}

      {/* Timestamp */}
      <div className={styles.timestamp}>
        Just now
      </div>

      {/* Character Counter */}
      <div className={styles.counterSection}>
        <CharacterCounter charCount={charCount} platformType="instagram" />
        <div className={styles.hashtagInfo}>
          Hashtags: {hashtags.length}/{specs?.hashtagLimit || 30}
        </div>
      </div>
    </div>
  );
};

InstagramPreview.propTypes = {
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    name: PropTypes.string,
    avatar_url: PropTypes.string,
  }),
  content: PropTypes.string,
  hashtags: PropTypes.arrayOf(PropTypes.string),
  imageUrl: PropTypes.string,
  onRemoveImage: PropTypes.func,
};

export default InstagramPreview;
